import React, { Component } from 'react';
import { connect } from 'react-redux';
import { readableStamp } from '../../utils/dateFunctions';
import './feedHistory.scss';

class FeedLogs extends Component {
	constructor(props) {
		super(props);

		this.state = {
			expandedRows: [],
		};
	}

	handleRowClick(rowId) {
		const { expandedRows } = this.state;

		this.setState({
			expandedRows: expandedRows.includes(rowId)
				? expandedRows.filter(id => id !== rowId)
				: expandedRows.concat(rowId),
		});
	}

	formatStatus(status) {
		if (!status) return 'Unknown';
		const label = status.replace(/_/g, ' ');
		return label.charAt(0).toUpperCase() + label.slice(1);
	}

	statusMark(status) {
		if (status === 'finished') return '✅';
		if (status === 'failed') return '❌';
		if (status === 'queued') return '⏳';
		return '';
	}

	formatTime(timestamp) {
		return timestamp ? readableStamp(timestamp) : '—';
	}

	formatDuration(startedAt, finishedAt) {
		if (!startedAt || !finishedAt) return '—';

		const seconds = Math.max(0, Math.round(
			(new Date(finishedAt).getTime() - new Date(startedAt).getTime()) / 1000,
		));
		if (seconds < 60) return `${seconds} sec`;

		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return remainingSeconds ? `${minutes} min ${remainingSeconds} sec` : `${minutes} min`;
	}

	formatCount(count) {
		return Number(count || 0).toLocaleString();
	}

	renderResults(feedLog) {
		if (!feedLog) return <span className="text-muted">No importer results</span>;

		return (
			<div>
				<div>{this.formatCount(feedLog.count)} total</div>
				<div>{this.formatCount(feedLog.count_in_feed)} in feed</div>
				<div>{this.formatCount(feedLog.count_created)} created</div>
				<div>{this.formatCount(feedLog.count_sold)} sold</div>
			</div>
		);
	}

	/** Booleans read as Yes/No, counts get thousands separators, text passes through. */
	formatValue(value) {
		if (typeof value === 'boolean') return value ? 'Yes' : 'No';
		if (typeof value === 'number') return value.toLocaleString();
		return value;
	}

	/** Pairs are [label, value]; anything null or blank is left out entirely. */
	renderDefinitions(pairs) {
		const present = pairs.filter(([, value]) => value != null && value !== '');
		if (!present.length) return null;

		return (
			<dl className="feed-history-definitions">
				{present.map(([label, value]) => [
					<dt key={`${label}-label`}>{label}</dt>,
					<dd key={`${label}-value`}>{this.formatValue(value)}</dd>,
				])}
			</dl>
		);
	}

	renderSection(title, pairs) {
		const definitions = this.renderDefinitions(pairs);
		if (!definitions) return null;

		return (
			<div className="feed-history-section">
				<h6>{title}</h6>
				{definitions}
			</div>
		);
	}

	renderRunDetails(feed) {
		return (
			<div className="feed-history-run">
				<h5>Run details</h5>
				{this.renderDefinitions([
					['Status', this.formatStatus(feed.status)],
					['Provider', feed.feed_source],
					['File', feed.feed_file],
					['S3 key', feed.s3_key],
					['Queued', feed.created_at && readableStamp(feed.created_at)],
					['Started', feed.started_at && readableStamp(feed.started_at)],
					['Finished', feed.finished_at && readableStamp(feed.finished_at)],
					['Duration', this.formatDuration(feed.started_at, feed.finished_at)],
					['Provider ID', feed.feed_provider_id],
					['Installation ID', feed.installation_id],
					['Error', feed.feed_error],
					['Skipped reason', feed.feed_skipped_reason],
				])}
			</div>
		);
	}

	renderImporterDetails(feedLog) {
		if (!feedLog) {
			return (
				<div>
					<h5>Importer results</h5>
					<p className="text-muted mb-0">No importer results linked to this run.</p>
				</div>
			);
		}

		return (
			<div>
				<h5>Importer results</h5>
				<div className="feed-history-tiles">
					{this.renderSection('Run', [
						['Status', feedLog.status && this.formatStatus(feedLog.status)],
						['Description', feedLog.description],
						['Started', feedLog.started_at && readableStamp(feedLog.started_at)],
						['Finished', feedLog.finished_at && readableStamp(feedLog.finished_at)],
					])}
					{this.renderSection('Inventory', [
						['Before feed', feedLog.count_pre_feed],
						['After feed', feedLog.count_post_feed],
						['Total', feedLog.count],
						['In feed', feedLog.count_in_feed],
						['Updated', feedLog.updatecount],
					])}
					{this.renderSection('In feed by type', [
						['New', feedLog.count_new_in_feed],
						['Used', feedLog.count_used_in_feed],
						['Wholesale', feedLog.count_wholesale_in_feed],
						['Misc', feedLog.count_misc_in_feed],
					])}
					{this.renderSection('Created', [
						['Total', feedLog.count_created],
						['New', feedLog.count_created_new],
						['Used', feedLog.count_created_used],
						['Wholesale', feedLog.count_created_wholesale],
						['Misc', feedLog.count_created_misc],
					])}
					{this.renderSection('Sold', [
						['Total', feedLog.count_sold],
						['New', feedLog.count_sold_new],
						['Used', feedLog.count_sold_used],
						['Wholesale', feedLog.count_sold_wholesale],
						['Misc', feedLog.count_sold_misc],
					])}
					{this.renderSection('Photos', [
						['Has images', feedLog.count_has_images],
						['No images', feedLog.count_no_images],
						['Fewer than ten', feedLog.count_less_than_ten_images],
					])}
					{this.renderSection('Other', [
						['Reconditioning', feedLog.count_reconditioning],
						['Purge override', feedLog.purge_override],
						['Duplicate VINs', feedLog.duplicate_vins],
						['Duplicate stocks', feedLog.duplicate_stocks],
						['Skipped vehicles', feedLog.skipped_vehicles],
						['Error', feedLog.feed_error],
						['Remedy', feedLog.feed_error_remedy],
					])}
				</div>
			</div>
		);
	}

	render() {
		const vehicleDataFeeds = Array.isArray(this.props.vehicle_data_feeds)
			? this.props.vehicle_data_feeds
			: [];
		const feedLogs = Array.isArray(this.props.feed_logs)
			? this.props.feed_logs
			: [];

		// VDF path keys by vehicle_data_feed_id; fallback keys by feed_logs.id.
		// Keep those exclusive — the two id sequences can collide.
		const feedLogsByRunId = new Map();
		if (vehicleDataFeeds.length) {
			feedLogs.forEach((feedLog) => {
				if (feedLog.vehicle_data_feed_id != null
					&& !feedLogsByRunId.has(String(feedLog.vehicle_data_feed_id))) {
					feedLogsByRunId.set(String(feedLog.vehicle_data_feed_id), feedLog);
				}
			});
		} else {
			feedLogs.forEach((feedLog) => {
				feedLogsByRunId.set(String(feedLog.id), feedLog);
			});
		}

		// IPR-478 listed only vehicle_data_feeds. Most stores still only have
		// feed_logs from the legacy importer, so fall back to those rows.
		const runs = vehicleDataFeeds.length
			? vehicleDataFeeds
			: feedLogs.map(feedLog => ({
				id: feedLog.id,
				status: feedLog.status,
				feed_source: feedLog.feed_source,
				feed_file: feedLog.feed_file,
				s3_key: feedLog.s3_key,
				started_at: feedLog.started_at || feedLog.created_at,
				finished_at: feedLog.finished_at,
				created_at: feedLog.created_at,
				feed_error: feedLog.feed_error,
				feed_skipped_reason: feedLog.feed_skipped_reason,
				feed_provider_id: feedLog.feed_provider_id,
				installation_id: feedLog.installation_id,
			}));

		if (!runs.length) {
			return <p className="text-center text-muted mb-0">No feed history available.</p>;
		}

		return (
			<div className="table-responsive">
				<table className="table table-striped align-top w-100">
					<thead>
						<tr>
							<th>Status</th>
							<th>Provider</th>
							<th>File</th>
							<th>Start Time</th>
							<th>End Time</th>
							<th>Duration</th>
							<th>Results</th>
						</tr>
					</thead>
					<tbody>
						{runs.map((feed) => {
							const feedLog = feedLogsByRunId.get(String(feed.id));
							const isExpanded = this.state.expandedRows.includes(feed.id);
							const toggle = () => this.handleRowClick(feed.id);
							const statusMark = this.statusMark(feed.status);
							const rows = [
								<tr
									key={`row-${feed.id}`}
									className="feed-history-row"
									onClick={toggle}
									onKeyDown={(event) => {
										if (event.key === 'Enter' || event.key === ' ') {
											event.preventDefault();
											toggle();
										}
									}}
									tabIndex={0}
									aria-expanded={isExpanded}
								>
									<td>
										<span
											className="feed-history-status"
											title={feed.feed_error || feed.feed_skipped_reason || ''}
										>
											{statusMark ? `${statusMark} ` : ''}
											{this.formatStatus(feed.status)}
										</span>
									</td>
									<td>{feed.feed_source || '—'}</td>
									<td>{feed.feed_file || feed.s3_key || '—'}</td>
									<td>{this.formatTime(feed.started_at)}</td>
									<td>{this.formatTime(feed.finished_at)}</td>
									<td>{this.formatDuration(feed.started_at, feed.finished_at)}</td>
									<td>{this.renderResults(feedLog)}</td>
								</tr>,
							];

							if (isExpanded) {
								rows.push(
									<tr key={`details-${feed.id}`} className="feed-history-details">
										<td colSpan="7">
											<div className="row">
												<div className="col-md-4">{this.renderRunDetails(feed)}</div>
												<div className="col-md-8">{this.renderImporterDetails(feedLog)}</div>
											</div>
										</td>
									</tr>,
								);
							}

							return rows;
						})}
					</tbody>
				</table>
			</div>
		);
	}
}

function mapStateToProps(state) {
	return {
		feed_logs: state.settings_feed.feed_logs,
		vehicle_data_feeds: state.settings_feed.vehicle_data_feeds,
	};
}
export default connect(mapStateToProps)(FeedLogs);
